/**
 * Platform-level route constants and shared route infrastructure.
 *
 * Contains:
 *  - Common path prefixes
 *  - Shared route types (`RouteConfig`, `RouteWithPath`)
 *  - `buildPath` helper for parameter substitution
 *  - Top-level platform routes (root, settings, state-machine routes)
 */

// Common path prefixes - helps ensure consistency
export const PREFIXES = {
  AUTH: "/auth",
  ORGANIZATIONS: "/organizations",
  PROJECTS: "/projects",
  APPLICATIONS: "/applications",
  USER: "/user",
  ACCOUNT: "/account",
  ONBOARDING: "/onboarding",
};

// Common route configuration type
export interface RouteConfig {
  path: string;
  // Navigation title (e.g., for breadcrumbs, menu items)
  title: string;
  // Additional metadata can be added here as needed
  exact?: boolean;
}

// Type definitions for route objects
export interface RouteWithPath {
  path: string;
  title: string;
}

// Function to build a path with parameters
type ParamValues = Record<string, string>;
export const buildPath = (basePath: string, params?: ParamValues): string => {
  if (!params) return basePath;

  return Object.entries(params).reduce(
    (path, [key, value]) => path.replace(`:${key}`, value),
    basePath,
  );
};

// Root paths
export const ROOT: RouteConfig = {
  path: "/",
  title: "Home",
  exact: true,
};

/** Deprecated compatibility path. The canonical landing is organization details. */
export const LEGACY_DASHBOARD: RouteConfig = {
  path: "/dashboard",
  title: "Legacy Dashboard",
  exact: true,
};

export const SETTINGS: RouteConfig = {
  path: "/settings",
  title: "System Settings",
  exact: true,
};

// Application state machine routes
export const LOADING: RouteConfig = {
  path: "/loading",
  title: "Loading",
  exact: true,
};

export const RECOVERY: RouteConfig = {
  path: "/recovery",
  title: "Recovery",
  exact: true,
};

export const ERROR: RouteConfig = {
  path: "/error",
  title: "Error",
  exact: true,
};

export const NOT_FOUND = "*";
